import React, { useState, useEffect } from "react";
import {
  Trophy,
  Loader2,
  FileDown,
  Trash2,
  Users,
  BookOpen,
  TrendingUp,
  Sparkles,
  CircleHelp,
  FileText,
  CheckCircle2,
} from "lucide-react";

// Import components
import ResultFilter from "./ResultFilter";
import ResultImportModal from "./ResultImportModal";
import StudentTranscriptModal from "./StudentTranscriptModal";
import Pagination from "../../../components/common/Pagination";
import {
  getResults,
  getResultStats,
  exportResultsCSV,
  deleteResult,
} from "../../../api/elearning/results/results";
import { showSuccessToast, showErrorToast } from "../../../components/ConfirmDialog";
import { confirmDelete, DELETE_ENTITY } from "../../../utils/confirmDelete";
import { hasPermission } from "../../../utils/permissionUtils";
import { startResultsPageTour } from "./resultsPageTour";

function readUserBranchId() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    return u.branch?.id || u.branch_id || "";
  } catch {
    return "";
  }
}

const SESSION_SUPER_ROLES = [
  "super admin",
  "superadmin",
  "administrator",
  "system administrator",
];

function isSuperAdminSession() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    const roles = Array.isArray(u.roles) ? u.roles : [];
    return roles
      .map((r) => String(r || "").trim().toLowerCase())
      .some((r) => SESSION_SUPER_ROLES.includes(r));
  } catch {
    return false;
  }
}

const ResultsPage = () => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentResults, setStudentResults] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [stats, setStats] = useState({ total_records: 0, total_classes: 0, total_students: 0, avg_score: null });
  const [isSuperAdmin] = useState(() => isSuperAdminSession());
  const [filters, setFilters] = useState(() => ({
    keyword: "",
    branch_id: isSuperAdminSession() ? "" : readUserBranchId(),
    course_id: "",
    class_id: "",
    exam_type: "",
    date_range: "",
  }));
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedResultIds, setSelectedResultIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const canQuickTour = hasPermission("elearning.results", "R");
  const canDeleteResult = hasPermission("elearning.results", "D");

  const stickyHead = "sticky z-30 bg-white";
  const stickyBody = "sticky z-20 bg-white";

  const fetchResults = async (overrides = {}) => {
    setLoading(true);
    try {
      const params = { page: currentPage, size: pageSize, ...filters, ...overrides };
      const res = await getResults(params);
      setStudentResults(res.records || []);
      setTotalRecords(res.total ?? 0);
    } catch {
      showErrorToast("Không thể tải danh sách bảng điểm");
      setStudentResults([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async (overrides = {}) => {
    try {
      const params = { ...filters, ...overrides };
      const res = await getResultStats(params);
      setStats(res);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    fetchResults();
  }, [
    currentPage,
    pageSize,
    filters.keyword,
    filters.branch_id,
    filters.course_id,
    filters.class_id,
    filters.exam_type,
    filters.date_range,
  ]);

  useEffect(() => {
    fetchStats();
  }, [
    filters.keyword,
    filters.branch_id,
    filters.course_id,
    filters.class_id,
    filters.exam_type,
    filters.date_range,
  ]);

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setCurrentPage(1);
    setSelectedResultIds([]);
  };

  const visibleResultIds = studentResults.map((r) => r.id).filter(Boolean);
  const selectedSet = new Set(selectedResultIds);
  const allVisibleSelected =
    canDeleteResult &&
    visibleResultIds.length > 0 &&
    visibleResultIds.every((id) => selectedSet.has(id));

  const toggleSelectOne = (id) => {
    if (!id || !canDeleteResult) return;
    setSelectedResultIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAllVisible = () => {
    if (!canDeleteResult) return;
    setSelectedResultIds((prev) => {
      if (allVisibleSelected) {
        return prev.filter((id) => !visibleResultIds.includes(id));
      }
      const next = [...prev];
      visibleResultIds.forEach((id) => {
        if (!next.includes(id)) next.push(id);
      });
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (!canDeleteResult || !selectedResultIds.length) return;
    const ids = [...selectedResultIds];
    const confirm = await confirmDelete({
      type: DELETE_ENTITY.exam_result_bulk,
      context: { count: ids.length },
    });
    if (!confirm.isConfirmed) return;

    setBulkDeleting(true);
    try {
      const settled = await Promise.allSettled(ids.map((id) => deleteResult(id)));
      const okCount = settled.filter((r) => r.status === "fulfilled").length;
      showSuccessToast(`Đã xóa ${okCount}/${ids.length} bản ghi điểm`);
      if (okCount < ids.length) {
        showErrorToast("Một số bản ghi không xóa được. Vui lòng thử lại.");
      }
      setSelectedResultIds([]);
      if (selectedStudent && ids.includes(selectedStudent.id)) {
        setIsModalOpen(false);
        setSelectedStudent(null);
      }
      fetchResults();
      fetchStats();
    } catch {
      showErrorToast("Không thể xóa hàng loạt bản ghi điểm");
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleRefreshRequest = (currentFilters) => {
    const merged = { ...filters, ...(currentFilters || {}) };
    fetchResults(merged);
    fetchStats(merged);
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const blob = await exportResultsCSV(filters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bang_diem_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      showSuccessToast("Đã xuất file CSV");
    } catch {
      showErrorToast("Không thể xuất file CSV");
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async (e, row) => {
    if (!canDeleteResult) return;
    e?.stopPropagation();
    const id = typeof row === "object" ? row?.id : row;
    const confirm = await confirmDelete({
      type: DELETE_ENTITY.exam_result,
      name: typeof row === "object" ? row?.student_name || row?.full_name : undefined,
    });
    if (!confirm.isConfirmed) return;
    try {
      await deleteResult(id);
      showSuccessToast("Đã xóa bản ghi");
      setSelectedResultIds((prev) => prev.filter((x) => x !== id));
      if (selectedStudent?.id === id) {
        setIsModalOpen(false);
        setSelectedStudent(null);
      }
      fetchResults();
      fetchStats();
    } catch {
      showErrorToast("Không thể xóa bản ghi");
    }
  };

  const handleImportSaved = () => {
    fetchResults();
    fetchStats();
  };

  const handleOpenTranscript = (student) => {
    setSelectedStudent(student);
    setIsModalOpen(true);
  };

  const handleQuickTour = () => {
    window.setTimeout(() => startResultsPageTour(), 80);
  };
  const openGuide = () => {
    const guideUrl = `${window.location.origin}/guides/elearning-results-guide.html`;
    window.open(guideUrl, "_blank", "noopener,noreferrer");
  };

  // Hàm lấy chữ cái đầu tiên của tên (dùng cho avatar)
  const getFirstLetterOfName = (fullName) => {
    if (!fullName) return "";
    const nameParts = fullName.trim().split(" ");
    const firstName = nameParts[nameParts.length - 1]; // Lấy chữ cuối cùng
    return firstName.charAt(0).toUpperCase();
  };

  const formatDateVN = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("vi-VN");
  };

  const formatClassTime = (raw) => {
    const value = String(raw || "").trim();
    if (!value) return "";
    if (!value.includes("|")) return value;
    const [days, slot] = value.split("|");
    return `${days?.split(",").join(", ") || ""}${slot ? ` • ${slot}` : ""}`.trim();
  };

  const splitAssessmentAndOutcome = (remarks, outcomeText) => {
    const rawRemarks = String(remarks || "").trim();
    const rawOutcome = String(outcomeText || "").trim();
    if (rawOutcome) {
      return {
        assessment: rawRemarks,
        outcome: rawOutcome,
      };
    }

    const match = rawRemarks.match(/^(.*?)(?:\r?\n)?Kết quả:\s*(.+)$/is);
    if (match) {
      return {
        assessment: String(match[1] || "").trim(),
        outcome: String(match[2] || "").trim(),
      };
    }

    return {
      assessment: rawRemarks,
      outcome: "",
    };
  };

  return (
    <div
      className={`min-h-screen bg-[#F8F9FA] ${
        isMobile ? "p-4" : "p-8"
      } text-left`}
    >
      {/* 1. Header & Quick Actions */}
      <div
        className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4"
        data-tour="results-header"
      >
        <div>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-600 rounded-2xl shadow-lg shadow-red-200">
              <Trophy className="text-white" size={24} />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-black text-gray-900 uppercase tracking-tight flex items-center gap-2 md:gap-3">
                Kết quả học tập
              </h1>
              <p className="text-sm text-gray-500 font-medium italic">
                Theo dõi và quản lý kết quả học tập của học viên tại tất cả chi
                nhánh
              </p>
            </div>
          </div>
        </div>
        {canQuickTour ? (
          <div className="flex items-center gap-2 self-start md:self-auto" data-tour="results-header-guides">
            <HeaderIconButton icon={Sparkles} label="Hướng dẫn nhanh" onClick={handleQuickTour} />
            <HeaderIconButton icon={CircleHelp} label="Hướng dẫn sử dụng" onClick={openGuide} />
          </div>
        ) : null}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-tour="results-stats">
        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-red-50 rounded-xl">
              <Trophy size={20} className="text-red-600" />
            </div>
            <div>
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Bản ghi điểm</p>
              <p className="text-xl font-black text-gray-800">{stats.total_records ?? 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-50 rounded-xl">
              <BookOpen size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Lớp có điểm</p>
              <p className="text-xl font-black text-gray-800">{stats.total_classes ?? 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-green-50 rounded-xl">
              <Users size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Học viên</p>
              <p className="text-xl font-black text-gray-800">{stats.total_students ?? 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-50 rounded-xl">
              <TrendingUp size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">ĐTB toàn hệ</p>
              <p className="text-xl font-black text-gray-800">{stats.avg_score != null ? stats.avg_score.toFixed(1) : "—"}</p>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Filter Section */}
      <div data-tour="results-filter">
        <ResultFilter
          defaultBranchId={filters.branch_id || (isSuperAdmin ? "" : readUserBranchId())}
          onFilterChange={handleFilterChange}
          onRefreshClick={handleRefreshRequest}
          onImportClick={() => setShowImport(true)}
          onExportClick={handleExportCSV}
        />
      </div>

      {canDeleteResult && selectedResultIds.length > 0 ? (
        <div className="flex justify-end mb-3">
          <button
            type="button"
            disabled={bulkDeleting}
            onClick={handleBulkDelete}
            className="px-4 py-2 rounded-xl bg-white border border-red-200 text-red-600 font-bold hover:bg-red-50 transition-all disabled:opacity-60"
          >
            {bulkDeleting
              ? "Đang xóa..."
              : `Xóa hàng loạt (${selectedResultIds.length})`}
          </button>
        </div>
      ) : null}

{/* 3. Data Table */}
<div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden relative isolate" data-tour="results-table">
  {loading && (
    <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10 rounded-xl">
      <Loader2 className="animate-spin text-red-600" size={32} />
    </div>
  )}
  <div className="overflow-x-auto">
    <table className="w-full text-left border-separate border-spacing-0">
      <thead>
      <tr className="bg-gray-50/50 border-b border-gray-100">
  {canDeleteResult ? (
    <th
      className={`p-4 w-[52px] min-w-[52px] text-center text-[11px] font-bold text-gray-400 uppercase tracking-wider ${stickyHead} left-0`}
    >
      <input
        type="checkbox"
        checked={allVisibleSelected}
        onChange={toggleSelectAllVisible}
        onClick={(e) => e.stopPropagation()}
        className="w-4 h-4 accent-red-600"
        aria-label="Chọn tất cả trên trang"
      />
    </th>
  ) : null}
  <th
    className={`p-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider text-center w-[60px] min-w-[60px] max-w-[60px] ${
      canDeleteResult
        ? `${stickyHead} left-[52px]`
        : `${stickyHead} left-0`
    }`}
  >
    Stt
  </th>

  <th
    className={`p-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider min-w-[240px] text-center border-r-2 border-gray-50 ${
      canDeleteResult
        ? `${stickyHead} left-[112px]`
        : `${stickyHead} left-[60px] border-r-2`
    }`}
  >
    Học viên
  </th>

  <th className="p-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider min-w-[200px]">
    Đơn vị
  </th>

  <th className="p-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider min-w-[240px]">
    Khóa học
  </th>

  <th className="p-4 text-[11px] font-bold text-red-500 uppercase tracking-wider text-center w-28 min-w-[100px] bg-red-50/70">
    Đầu vào
  </th>
  <th className="p-4 text-[11px] font-bold text-red-500 uppercase tracking-wider text-center w-28 min-w-[100px] bg-red-50/70">
    Đầu ra
  </th>
  <th className="p-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider min-w-[320px]">
    Đánh giá & Kết quả
  </th>

  <th className="p-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider text-center w-32 min-w-[120px] sticky right-0 z-20 bg-white border-l-2 border-gray-50">
    Tác vụ
  </th>
</tr>
      </thead>
      <tbody className="divide-y divide-gray-50">
        {studentResults.map((res, index) => (
          <tr
            key={res.id}
            className="hover:bg-red-50/20 transition-all cursor-pointer group"
            onClick={() => handleOpenTranscript(res)}
          >
            {canDeleteResult ? (
              <td
                className={`p-4 w-[52px] min-w-[52px] text-center ${stickyBody} left-0`}
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={selectedSet.has(res.id)}
                  onChange={() => toggleSelectOne(res.id)}
                  onClick={(e) => e.stopPropagation()}
                  className="w-4 h-4 accent-red-600"
                  aria-label={`Chọn bản ghi ${res.full_name || res.id}`}
                />
              </td>
            ) : null}
            <td
              className={`p-4 w-[60px] min-w-[60px] max-w-[60px] text-center ${
                canDeleteResult ? `${stickyBody} left-[52px]` : `${stickyBody} left-0`
              }`}
            >
              <span className="text-xs font-medium text-gray-400">
                {(currentPage - 1) * pageSize + index + 1}
              </span>
            </td>

<td
  className={`p-4 min-w-[240px] border-r-2 border-gray-50 ${
    canDeleteResult ? `${stickyBody} left-[112px]` : `${stickyBody} left-[60px]`
  }`}
>
  <div className="flex items-center gap-3 text-left">
    <div className="w-10 h-10 rounded-xl bg-gray-50 text-gray-400 flex items-center justify-center font-bold group-hover:bg-red-600 group-hover:text-white transition-all shrink-0 border border-gray-100 shadow-sm">
      {/* Gọi hàm helper để lấy chữ cái đầu của Tên chính */}
      {getFirstLetterOfName(res.full_name)}
    </div>
    <div className="flex flex-col">
      <span className="font-bold text-gray-900 text-sm uppercase leading-tight tracking-tight">
        {res.full_name}
      </span>
      <span className="text-[10px] text-gray-500 font-semibold">
        {res.class_name || "—"}
      </span>
    </div>
  </div>
</td>

            <td className="p-4">
              <div className="flex flex-col">
                <span className="text-xs font-semibold text-gray-600 uppercase">
                  {res.branch_name}
                </span>
                {(res.class_type === "school" && res.school_name) ? (
                  <span className="text-[10px] text-gray-400 font-normal italic">
                    {res.school_name}
                  </span>
                ) : null}
              </div>
            </td>

            <td className="p-4">
              <div className="flex flex-col">
                <span className="text-xs font-bold text-gray-700 italic">
                  {res.course_name || res.class_name}
                </span>
                <span className="text-[10px] font-medium text-gray-400">
                  {`${formatDateVN(res.start_date)} - ${formatDateVN(res.end_date)}`}
                </span>
              </div>
            </td>

            <td className="p-4 text-center font-mono font-black text-red-600 bg-red-50/40">
              {res.entrance_score != null ? res.entrance_score : "—"}
            </td>
            <td className="p-4 text-center font-mono font-black text-red-600 bg-red-50/40">
              {res.exit_score != null ? res.exit_score : "—"}
            </td>
            <td className="p-4 text-[11px] text-gray-600 min-w-[320px]">
              {(() => {
                const detail = splitAssessmentAndOutcome(res.remarks, res.outcome_text);
                return (
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <FileText
                        size={14}
                        className="mt-0.5 shrink-0 text-slate-400"
                        title="Đánh giá"
                      />
                      <p
                        className="leading-relaxed text-gray-700 whitespace-pre-wrap break-words"
                        title={detail.assessment || ""}
                      >
                        {detail.assessment || "—"}
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2
                        size={14}
                        className="mt-0.5 shrink-0 text-emerald-500"
                        title="Kết quả"
                      />
                      <p
                        className="leading-relaxed text-gray-600 whitespace-pre-wrap break-words"
                        title={detail.outcome || ""}
                      >
                        {detail.outcome || "—"}
                      </p>
                    </div>
                  </div>
                );
              })()}
            </td>

           <td className="p-4 text-center sticky right-0 z-10 bg-white border-l-2 border-gray-50">
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenTranscript(res);
                  }}
                  className="p-2 hover:bg-blue-50 rounded-xl transition-all group/edit border border-transparent hover:border-blue-100 shadow-sm bg-white"
                  title="Tải bảng điểm"
                >
                  <FileDown
                    size={16}
                    className="text-gray-400 group-hover/edit:text-blue-600 transition-colors"
                  />
                </button>
                {canDeleteResult ? (
                  <button
                    onClick={(e) => handleDelete(e, res)}
                    className="p-2 hover:bg-red-50 rounded-xl transition-all group/delete border border-transparent hover:border-red-100 shadow-sm bg-white"
                    title="Xóa kết quả"
                  >
                    <Trash2
                      size={16}
                      className="text-gray-400 group-hover/delete:text-red-600 transition-colors"
                    />
                  </button>
                ) : null}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
</div>

      {/* 4. Pagination (Sử dụng component bạn cung cấp) */}
      {!loading && totalRecords > 0 && (
        <div className="relative z-50 mt-4">
          <Pagination
            totalRecords={totalRecords}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={(p) => {
              setCurrentPage(p);
              setSelectedResultIds([]);
            }}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setSelectedResultIds([]);
            }}
          />
        </div>
      )}

      {/* Modals */}
      <StudentTranscriptModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        student={selectedStudent}
        onSaved={(updated) => {
          if (updated) setSelectedStudent((prev) => (prev ? { ...prev, ...updated } : null));
          fetchResults();
          fetchStats();
        }}
      />
      <ResultImportModal
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        onRefresh={handleImportSaved}
      />
    </div>
  );
};

export default ResultsPage;

function HeaderIconButton({ icon: Icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="group relative inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-red-200 bg-white text-red-700 shadow-sm transition-all hover:bg-red-50 hover:border-red-300"
    >
      <Icon className="h-5 w-5" strokeWidth={2.2} />
      <span className="pointer-events-none absolute bottom-full right-0 mb-2 rounded-lg border border-red-100 bg-white px-2.5 py-1 text-[11px] font-bold text-red-700 whitespace-nowrap opacity-0 translate-y-1 shadow-md transition-all group-hover:opacity-100 group-hover:translate-y-0">
        {label}
      </span>
    </button>
  );
}
